import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  PropsWithChildren,
} from 'react';
import { Scorecard, ScorecardStores } from 'store/interfaces';

type ScorecardMutation = (
  scorecard: Scorecard,
) => Promise<Scorecard | undefined>;

interface ScorecardContextValues {
  id: number;
  store: ScorecardStores;
  isArchived?: boolean;
  isDeleted?: boolean;
  hasDirtyInlineEditors: boolean;
  setInlineEditorDirty: (editorId: string, isDirty: boolean) => void;
  enqueueScorecardMutation: (
    scorecard: Scorecard,
    mutation: ScorecardMutation,
  ) => Promise<Scorecard | undefined>;
}

type ScorecardProviderProps = Pick<
  ScorecardContextValues,
  'id' | 'store' | 'isArchived' | 'isDeleted'
>;

const ScorecardContext = createContext<ScorecardContextValues>(
  {} as unknown as ScorecardContextValues,
);

export function useScorecardContext() {
  return useContext<ScorecardContextValues>(ScorecardContext);
}

export function ScorecardProvider({
  children,
  id,
  store,
  isArchived,
  isDeleted,
}: PropsWithChildren<ScorecardProviderProps>) {
  const queueRef = useRef<Promise<void>>(Promise.resolve());
  const latestScorecardRef = useRef<Scorecard>();
  const dirtyEditorIdsRef = useRef(new Set<string>());
  const [hasDirtyInlineEditors, setHasDirtyInlineEditors] = useState(false);

  const setInlineEditorDirty = useCallback(
    (editorId: string, isDirty: boolean) => {
      if (isDirty) {
        dirtyEditorIdsRef.current.add(editorId);
      } else {
        dirtyEditorIdsRef.current.delete(editorId);
      }
      setHasDirtyInlineEditors(dirtyEditorIdsRef.current.size > 0);
    },
    [],
  );

  const enqueueScorecardMutation = useCallback(
    (scorecard: Scorecard, mutation: ScorecardMutation) => {
      const queuedMutation = queueRef.current.then(async () => {
        const latestScorecard = latestScorecardRef.current;
        const sourceScorecard =
          latestScorecard &&
          latestScorecard.id === scorecard.id &&
          latestScorecard.version >= scorecard.version
            ? latestScorecard
            : scorecard;
        const updatedScorecard = await mutation(sourceScorecard);
        if (updatedScorecard) {
          latestScorecardRef.current = updatedScorecard;
        }
        return updatedScorecard;
      });

      queueRef.current = queuedMutation.then(
        () => undefined,
        () => undefined,
      );
      return queuedMutation;
    },
    [],
  );

  const value = useMemo(
    () => ({
      id,
      store,
      isArchived,
      isDeleted,
      hasDirtyInlineEditors,
      setInlineEditorDirty,
      enqueueScorecardMutation,
    }),
    [
      id,
      store,
      isArchived,
      isDeleted,
      enqueueScorecardMutation,
      hasDirtyInlineEditors,
      setInlineEditorDirty,
    ],
  );

  return (
    <ScorecardContext.Provider value={value}>
      {children}
    </ScorecardContext.Provider>
  );
}
